/**
 * Что показать игроку, который ещё не играл.
 *
 * Снимок без подписей и карта мира выглядят понятно ровно до момента, когда
 * надо что-то сделать. Отдельного признака «прошёл обучение» для этого не
 * нужно: сервер и так знает, сколько партий сыграно, и первая партия — это
 * та, перед которой их ноль.
 */

import type { UserProfile } from "~/api/types";
import type { GameSetup } from "~/domain/setup";

/** Ещё ни одной законченной партии. */
export function isNewPlayer(user: UserProfile | null): boolean {
  return user !== null && user.games_played === 0;
}

/**
 * Настройки первой партии.
 *
 * Она должна быть узнаваемой, а не честной: человек, который первым же
 * раундом получил пустыню в кадре шириной пятнадцать километров, второй
 * партии не начинает. Дальше он выставит всё сам.
 */
export const FIRST_GAME_SETUP: GameSetup = {
  rounds: 5,
  level: "easy",
  place: null,
  timeLimit: null,
  answerMode: "point",
};

/** Шаг подсказки в первом раунде. */
export type CoachStep = "look" | "map" | "answer";

/**
 * Какой шаг показывать. Выводится из того, что игрок уже сделал, а не из
 * счётчика нажатий: подсказка, которая висит после выполненного действия,
 * раздражает сильнее, чем её отсутствие.
 *
 * Раскрытая игроком карта закрывает второй шаг целиком: он про то, как её
 * открыть, и висеть поверх уже открытой карты ему незачем. На телефоне это
 * ещё и вопрос места — там раскрытая карта занимает пол-экрана, и длинная
 * карточка над ней накрывала бы собственные кнопки.
 */
export function coachStep(acknowledged: boolean, mapOpen: boolean, hasGuess: boolean): CoachStep {
  // Поставленная точка сильнее всего: объяснять «осмотрись» тому, кто уже
  // ответил, — это подсказка, отставшая от игрока
  if (hasGuess) return "answer";
  if (!acknowledged) return "look";

  return mapOpen ? "answer" : "map";
}
